import * as React from 'react';

import {Button} from 'shells/button/button';
import {SelectedFieldCard} from 'shells/form/sidebar-fields/selected-field-card';
import {CirclePlusRegularIcon} from 'shells/icon/svg/circle-plus-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';

import {GraphQLSelectUsers} from '../../../core/select/graphql-select-users';

import {NOTIFIABLE_STATIC_OPTIONS, isNotifiableAlias} from '../../utils/notifiable-options';

export type SelectedNotifiable = {
    id: string;
    label: string;
    isAlias: boolean;
};

type Props = {
    selectedNotifiables: SelectedNotifiable[];
    onChange: (notifiables: SelectedNotifiable[]) => void;
};

export function NotifiableSelectList(props: Props): React.ReactElement {
    const {selectedNotifiables, onChange} = props;
    const [isAddingNew, setIsAddingNew] = React.useState<boolean>(selectedNotifiables.length === 0);

    const handleSelectChange = (selection: ListOptionWithIcon) => {
        if (!selection) {
            return;
        }

        const newNotifiable: SelectedNotifiable = {
            id: selection.value,
            label: selection.label,
            isAlias: isNotifiableAlias(selection.value),
        };
        onChange([...selectedNotifiables, newNotifiable]);
        setIsAddingNew(false);
    };

    const handleRemove = (index: number) => {
        const newNotifiables = selectedNotifiables.filter(
            (notifiable: SelectedNotifiable, i: number) => i !== index
        );
        onChange(newNotifiables);
        if (newNotifiables.length === 0) {
            setIsAddingNew(true);
        }
    };

    const handleAddAnother = () => {
        setIsAddingNew(true);
    };

    const excludeIds = selectedNotifiables.map((n) => n.id);
    const availableStaticOptions = NOTIFIABLE_STATIC_OPTIONS.filter(
        (option) => !excludeIds.includes(option.value)
    );

    return (
        <Stack spacing={4}>
            {selectedNotifiables.map((notifiable, index) => (
                <SelectedFieldCard
                    key={notifiable.id}
                    name={notifiable.label}
                    onRemove={() => handleRemove(index)}
                />
            ))}
            {isAddingNew ? (
                <GraphQLSelectUsers
                    selectedId=''
                    isMulti={false}
                    valueKey='value'
                    labelKey='label'
                    onChange={handleSelectChange}
                    staticOptions={availableStaticOptions}
                    filterOption={(
                        option: {
                            data: {
                                value: string;
                                label: string;
                            };
                        },
                        inputValue: string
                    ) => {
                        // need to exclude already selected options
                        if (excludeIds.includes(option.data.value)) {
                            return false;
                        }

                        // default search behavior overridden by filterOption, so we need to add it here
                        if (!inputValue) {
                            return true;
                        }

                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />
            ) : (
                <Button
                    variant='text-primary'
                    size='normal'
                    icon={CirclePlusRegularIcon}
                    onClick={handleAddAnother}
                    noPadding={true}
                >
                    Add another person
                </Button>
            )}
        </Stack>
    );
}
